import { useState, useCallback, type FormEvent } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card';
import { AlertCircleIcon, MailIcon } from '@/components/icons';
import { getApiErrorMessage, isApiError } from '@/api/errors';
import { isValidEmail } from '@/utils/validation';
import './Login.css';

/**
 * Form validation errors
 */
interface FormErrors {
  email?: string;
  password?: string;
}

/**
 * Login page component with form and error handling
 *
 * Features:
 * - Email/password authentication
 * - Client-side validation
 * - Server-side error handling
 * - Loading state during submission
 * - Redirect to dashboard on success
 * - Preserves intended destination after login
 */
export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, isInitializing: authLoading } = useAuth();

  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get the redirect destination from location state (set by ProtectedRoute)
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/';

  /**
   * Validate form fields
   */
  const validateForm = useCallback((): boolean => {
    const newErrors: FormErrors = {};

    // Validate email
    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!isValidEmail(email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Validate password
    if (!password) {
      newErrors.password = 'Password is required';
    } else if (password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [email, password]);

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();

      // Clear previous server error
      setServerError(null);

      // Validate form
      if (!validateForm()) {
        return;
      }

      setIsSubmitting(true);

      try {
        await login({ email: email.trim(), password });

        // Redirect to the original destination or dashboard
        navigate(from, { replace: true });
      } catch (error) {
        if (isApiError(error) && error.status === 401) {
          setServerError('Invalid email or password');
        } else {
          setServerError(
            getApiErrorMessage(error, 'An error occurred during login. Please try again.')
          );
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [email, password, validateForm, login, navigate, from]
  );

  /**
   * Clear field error when user starts typing
   */
  const handleEmailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setEmail(e.target.value);
      if (errors.email) {
        setErrors((prev) => ({ ...prev, email: undefined }));
      }
      if (serverError) {
        setServerError(null);
      }
    },
    [errors.email, serverError]
  );

  const handlePasswordChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setPassword(e.target.value);
      if (errors.password) {
        setErrors((prev) => ({ ...prev, password: undefined }));
      }
      if (serverError) {
        setServerError(null);
      }
    },
    [errors.password, serverError]
  );

  // If already authenticated, redirect to dashboard
  if (isAuthenticated && !authLoading) {
    return <Navigate to={from} replace />;
  }

  // Show loading state during auth initialization
  if (authLoading) {
    return (
      <div className="login-page">
        <div className="login-loading">
          <div className="login-spinner" />
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-container">
        <Card variant="bordered" className="login-card">
          <CardHeader className="login-card-header">
            <div className="login-logo">
              <MailIcon />
            </div>
            <CardTitle as="h1" className="login-title">
              CDN Manager
            </CardTitle>
            <CardDescription className="login-subtitle">
              Sign in to access the admin dashboard
            </CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="login-form" noValidate>
              {/* Server Error Alert */}
              {serverError && (
                <div className="login-alert login-alert-error" role="alert">
                  <AlertCircleIcon />
                  <span>{serverError}</span>
                </div>
              )}

              {/* Email Input */}
              <Input
                label="Email"
                type="email"
                placeholder="admin@example.com"
                value={email}
                onChange={handleEmailChange}
                error={errors.email}
                required
                autoComplete="email"
                autoFocus
                disabled={isSubmitting}
              />

              {/* Password Input */}
              <Input
                label="Password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={handlePasswordChange}
                error={errors.password}
                required
                autoComplete="current-password"
                disabled={isSubmitting}
                showPasswordToggle
              />

              {/* Submit Button */}
              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                isLoading={isSubmitting}
                className="login-submit-btn"
              >
                Sign In
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="login-footer-text">
          CDN Management Platform &copy; {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
}
